// ONE CONTRACTOR'S LINE ITEMS: where every hour came from, what it cost, and
// the total at the bottom.
//
// Owner, 2026-10-09: "every contractors his full report broken down into rows
// with details where the hours came from and cost per line item and total at
// the bottom, reference each sheet, issue, or other hourly deliverable".
//
// THE TOTAL AT THE BOTTOM IS THE SUM OF THE ROWS ABOVE IT, added up here from
// what is shown. The server builds the contractor's total in the same pass as
// the rows, so the two always agree; if they ever do not, this says so in red
// rather than letting a reader find it with a calculator.
//
// A row that cannot be attributed (no company, no person, item deleted) is
// listed with the reason beside it - never hidden.
//
// THE RATE ON EACH LINE CAN BE SET FOR THAT ITEM (owner, 2026-10-10: "the ui
// should allow to override the rate per item and actually remember the
// unique item's custom rate"). A company admin or super admin gets a pencil
// beside the rate; what was recorded at submission stays visible under it.
//
// AND A GROUP OF LINES CAN TAKE ONE RATE (owner, 2026-10-11: "add checkbox
// group rate edit on filtered results"). The checkboxes are here; the
// selection itself lives on the page, because one selection spans every
// contractor in the filtered report. Only lines that can take a rate get a
// box - the same rule as the pencil, from isRateEditable.
//
// AND A LINE CAN BE "NO COST" (owner, 2026-10-12: "make the same option, works
// for each item individually, to hide its rate and show $0 (no cost) as a
// checkbox"). The hours stay in every total; the rate is not shown and the
// cost is $0.00. That is NOT the Ignore action, which takes the line out of
// the report altogether - the tooltip on the box says so.
import React, { useState } from "react";
import { Alert, Button, Checkbox, InputNumber, Popover, Space, Table, Tag, Tooltip, message } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { removeItemOverride, setItemOverride, setItemsNoCost } from "api/billableHours";
import { WhoSeesRate } from "./rateAudience";
import { withSorting, useRememberedSort } from "helpers/tableSort";

const T = { muted: "#667085", amber: "#b54708", red: "#b42318" };
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const hrs = (n) => `${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} h`;
const day = (v) => (v ? new Date(v).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "–");
const r2 = (n) => Math.round(n * 100) / 100;

// CAN THIS LINE TAKE A RATE? Issues and shop drawings with hours. A paired
// drawing's hours are counted on its issue, so there is nothing on it to
// price - the server's pricing skips it too.
export const isRateEditable = (r) => !!r && r.hours != null && r.rateSource !== "paired"
  && (r.system === "issues" || r.system === "shopDrawings");

// The item rate's own key: one item, one contractor. Two ledger lines on the
// same issue for the same contractor share it, and are selected together,
// because one rate prices both.
export const itemKey = (r) => `${r.system}:${r.ref}:${r.companyId || ""}`;

// The values utils/issueHours.rateFor stamps, worded as its own SOURCE map
// words them on the issue export, plus the two billableHours adds.
// EXPORTED since 2026-10-26 so the "Rates used" card on the page reads THIS
// map rather than becoming a fourth copy of the same vocabulary - the very
// thing the note below warns about.
export const RATE_SOURCE = {
  user: "own rate",
  supervisor: "own rate",
  partner: "company default",
  account: "account rate",
  paired: "counted on the paired issue",
  none: "no rate set when logged",
  "": "no rate set when logged",
  "override-item": "rate set for this item",
  // THE PAGE IS THE THIRD READER OF THESE AND IT WAS THE ONE I MISSED. The
  // exports and the email both got the new words when the activity rate shipped
  // (utils/contractorReportExport RATE_SOURCE, utils/contractorReportSend
  // RATE_WORDS); this map did not, so a line priced by an activity read the raw
  // key "override-activity" on prod. Three copies of one vocabulary is the
  // problem - noted here so the next rate source is added to all three. (The
  // page's own Rates used card is NOT a fourth: it imports this map.)
  "override-activity": "rate set for this schedule activity",
  "override-report": "rate set for this report",
  "override-project": "contractor rate, this project",
  "override-account": "contractor rate, every project",
  "no-cost": "no cost",
};

// Shown on every no-cost box, so nobody confuses it with Ignore.
export const NO_COST_HELP = "No cost: the hours stay on the report and in every total, the rate is hidden and the cost shows $0.00 - here, in the exports and in the report emails. Untick to price it again. Not the same as Ignore, which takes the line out of the report altogether.";

// The item's own dates (server: utils/billableRows itemDates), labelled per
// kind: Created / Resolved for an issue, Requested / Delivered (+ Approved)
// for a shop drawing.
function DateCell({ label, value, extra = null }) {
  if (!value) return <span style={{ color: T.muted }}>–</span>;
  return (
    <span>
      <div style={{ color: T.muted, fontSize: 11 }}>{label}</div>
      <div>{day(value)}</div>
      {extra}
    </span>
  );
}

// ONE LINE'S NO-COST BOX. A line covered by its contractor's no-cost mark
// shows ticked and greyed: it is the contractor's box that undoes it.
function NoCostBox({ row, companyName, onChanged }) {
  const [busy, setBusy] = useState(false);
  const byContractor = row.noCost && row.noCostScope === "contractor";
  const checked = !!row.noCost;
  const toggle = (e) => {
    const next = e.target.checked;
    setBusy(true);
    setItemsNoCost([{ system: row.system, ref: row.ref, contractorCompanyId: row.companyId || "", contractorName: companyName || "" }], next)
      .then(() => { message.success(next ? "Marked no cost" : "Priced again"); if (onChanged) onChanged(); })
      .catch((err) => message.error((err && (err.err || err.message)) || "Could not change it"))
      .finally(() => setBusy(false));
  };
  const who = row.noCost && row.noCostBy ? ` Set by ${row.noCostBy}${row.noCostAt ? ` on ${day(row.noCostAt)}` : ""}.` : "";
  return (
    <Tooltip title={byContractor ? `The whole contractor is no cost on this project (the box beside their name).${who}` : `${NO_COST_HELP}${who}`}>
      <Checkbox checked={checked} disabled={busy || byContractor} onChange={toggle} aria-label="No cost for this item" />
    </Tooltip>
  );
}

// ONE LINE'S RATE EDITOR. Saves a rate for this item and this contractor;
// the server remembers it and every cost that reads the line - this page,
// the exports, the digests - uses it from then on.
function ItemRateEditor({ row, companyName, onChanged }) {
  const [open, setOpen] = useState(false);
  const [rate, setRate] = useState(null);
  const [appliesTo, setAppliesTo] = useState("owner");
  const [busy, setBusy] = useState(false);
  const isItem = row.overrideScope === "item";

  const start = (v) => {
    if (v) {
      setRate(row.rate == null ? null : Number(row.rate));
      setAppliesTo(isItem && row.overrideAppliesTo === "everyone" ? "everyone" : "owner");
    }
    setOpen(v);
  };
  const save = () => {
    setBusy(true);
    setItemOverride({
      system: row.system, ref: row.ref, contractorCompanyId: row.companyId || "",
      contractorName: companyName || "", rate, appliesTo,
    })
      .then(() => { message.success("Rate saved for this item"); setOpen(false); if (onChanged) onChanged(); })
      .catch((e) => message.error((e && (e.err || e.message)) || "Could not save the rate"))
      .finally(() => setBusy(false));
  };
  const clear = () => {
    setBusy(true);
    removeItemOverride(row.overrideId)
      .then(() => { message.success("Item rate removed"); setOpen(false); if (onChanged) onChanged(); })
      .catch((e) => message.error((e && (e.err || e.message)) || "Could not remove it"))
      .finally(() => setBusy(false));
  };

  const content = (
    <div style={{ width: 300, display: "grid", gap: 8 }}>
      <div style={{ color: T.muted, fontSize: 12 }}>
        Hourly rate for {companyName || "this contractor"} on this {(row.item && row.item.kind ? row.item.kind : "item").toLowerCase()} only.
        It comes before the contractor&apos;s project and account rates.
      </div>
      <InputNumber
        autoFocus style={{ width: "100%" }} min={0} max={10000} prefix="$" addonAfter="/h"
        value={rate} onChange={setRate} onPressEnter={() => rate != null && save()}
      />
      <WhoSeesRate value={appliesTo} onChange={setAppliesTo} compact />
      <Space style={{ justifyContent: "space-between", width: "100%" }}>
        {isItem
          ? <Button size="small" danger disabled={busy} onClick={clear}>Remove item rate</Button>
          : <span />}
        <Space>
          <Button size="small" onClick={() => setOpen(false)}>Cancel</Button>
          <Button size="small" type="primary" loading={busy} disabled={rate == null} onClick={save}>Save</Button>
        </Space>
      </Space>
    </div>
  );
  return (
    <Popover trigger="click" open={open} onOpenChange={start} content={content} title="Rate for this item" placement="left">
      <Tooltip title={isItem ? "Change this item's rate" : "Set a rate for this item"}>
        <Button size="small" type="text" icon={<EditOutlined />} aria-label="Set a rate for this item" />
      </Tooltip>
    </Popover>
  );
}

// WHAT EACH LINE COLUMN SORTS ON - what its cell shows. "Not entered" hours,
// an unpriced cost and a missing date are empty cells (last both ways); a
// no-cost line sorts as $0, which is what it reads.
const when = (v) => { const t = v ? Date.parse(v) : NaN; return Number.isNaN(t) ? null : t; };
export const ROW_SORT = {
  at: (r) => when(r.at),
  opened: (r) => when(r.dates && r.dates.opened),
  closed: (r) => when(r.dates && r.dates.closed),
  p: (r) => r.projectTitle || r.projectId || null,
  item: (r) => { const it = r.item || {}; return [it.label, it.title].filter(Boolean).join(" ") || null; },
  createdBy: (r) => r.createdBy || null,
  assignedTo: (r) => r.assignedTo || null,
  who: (r) => r.person || null,
  h: (r) => (r.hours == null ? null : Number(r.hours)),
  rate: (r) => (r.noCost ? 0 : (r.rate == null ? null : Number(r.rate))),
  cost: (r) => (r.hours == null || (r.cost == null && !r.noCost) ? null : (r.noCost ? 0 : Number(r.cost))),
  nocost: (r) => (r.noCost ? 1 : 0),
};

export default function ContractorRows({ contractor, canEdit = false, onChanged, selected = null, onSelectRows = null, estimated = false }) {
  // The cost columns are only there when the report carries money.
  const withCost = (contractor.rows || []).some((r) => "cost" in r) || "cost" in contractor;
  const rows = contractor.rows || [];
  // The table needs a unique key per line; the selection is by item key.
  const tableKey = (r, i) => `${r.system}:${r.ref}:${i}`;
  const keyed = rows.map((r, i) => ({ ...r, _k: tableKey(r, i) }));
  const selectable = canEdit && !!onSelectRows && !!selected;
  const rowSelection = selectable ? {
    selectedRowKeys: keyed.filter((r) => selected.has(itemKey(r))).map((r) => r._k),
    getCheckboxProps: (r) => ({
      disabled: !isRateEditable(r),
      title: isRateEditable(r) ? "" : (r.rateSource === "paired" ? "Counted on its paired issue" : "No hours to price"),
    }),
    onSelect: (r, checked) => onSelectRows([r], checked, contractor),
    onSelectAll: (checked, _all, changed) => onSelectRows(changed.filter(isRateEditable), checked, contractor),
  } : undefined;
  const sumHours = r2(rows.reduce((a, r) => a + (r.hours == null ? 0 : Number(r.hours)), 0));
  const sumCost = r2(rows.reduce((a, r) => a + (r.cost == null ? 0 : Number(r.cost)), 0));
  // What the ledgers recorded, for the "recorded" note under the total when
  // an override changed it.
  const sumRecorded = r2(rows.reduce((a, r) => a + Number(("recordedCost" in r ? r.recordedCost : r.cost) || 0), 0));
  const unpriced = rows.filter((r) => r.hours != null && r.cost == null).length;
  // Listed, and adding nothing: said in the total line so a reader adding up
  // the column knows which lines are in it.
  const noHours = rows.filter((r) => r.hours == null).length;
  const noCostLines = rows.filter((r) => r.noCost && r.hours != null).length;
  const disagree = sumHours !== r2(contractor.hours || 0) || sumCost !== r2(contractor.cost || 0);

  // EVERY HEADER SORTS (owner, 2026-10-23), on what the cell shows; one
  // remembered order for every contractor's lines (helpers/tableSort). The
  // total line under them is the sum whatever the order.
  const [rowSort, onRowSort] = useRememberedSort("contractorCost.rowSort");
  const columns = withSorting([
    { title: estimated ? "Created" : "Hours recorded", dataIndex: "at", key: "at", width: 110, render: day },
    {
      title: "Created / requested", key: "opened", width: 110,
      render: (_, r) => <DateCell label={(r.dates && r.dates.openedLabel) || ""} value={r.dates && r.dates.opened} />,
    },
    {
      title: "Resolved / delivered", key: "closed", width: 120,
      render: (_, r) => (
        <DateCell
          label={(r.dates && r.dates.closedLabel) || ""} value={r.dates && r.dates.closed}
          extra={r.dates && r.dates.approved ? <div style={{ color: T.muted, fontSize: 11 }}>approved {day(r.dates.approved)}</div> : null}
        />
      ),
    },
    { title: "Project", dataIndex: "projectTitle", key: "p", render: (v, r) => v || <span style={{ color: T.muted }}>{r.projectId}</span> },
    {
      title: "Where the hours came from", key: "item",
      render: (_, r) => {
        const it = r.item || {};
        return (
          <div>
            <span style={{ color: T.muted }}>{it.kind}</span>{" "}
            <b>{it.label || "—"}</b>
            {it.title ? <span> · {it.title}</span> : null}
            {/* The id is always printed: it is what lets a reader quote this
                exact line to somebody who can open the item. */}
            <div style={{ color: T.muted, fontSize: 11, fontFamily: "monospace" }}>id {it.id}</div>
            {r.system === "shopDrawings" && r.rateSource === "paired" && r.detail ? (
              <div style={{ color: T.muted, fontSize: 12 }}>{r.detail}</div>
            ) : null}
          </div>
        );
      },
    },
    // ESTIMATED LINES (owner, 2026-10-16): who raised the item and who it is
    // on, each as "Company / person" - an estimate has no timesheet person.
    ...(estimated ? [
      { title: "Created by", dataIndex: "createdBy", key: "createdBy", render: (v) => v || <span style={{ color: T.muted }}>-</span> },
      { title: "Assigned to", dataIndex: "assignedTo", key: "assignedTo", render: (v) => v || <span style={{ color: T.muted }}>nobody yet</span> },
    ] : [
      { title: "Person", dataIndex: "person", key: "who", render: (v) => v || <span style={{ color: T.muted }}>not recorded</span> },
    ]),
    {
      title: estimated ? "Est. hours" : "Hours", dataIndex: "hours", key: "h", align: "right", width: 90,
      render: (v) => (v == null
        ? <Tooltip title="Submitted without hours - not zero hours, simply not entered."><span style={{ color: T.muted }}>not entered</span></Tooltip>
        : hrs(v)),
    },
    {
      title: "Rate", dataIndex: "rate", key: "rate", align: "right", width: 170,
      render: (v, r) => {
        // A paired drawing's hours are on its issue: nothing here to price.
        const editable = canEdit && isRateEditable(r);
        const overridden = !!r.overrideScope;
        if (r.noCost) {
          return (
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "flex-end", gap: 2 }}>
              <span>
                <span style={{ color: T.muted, fontWeight: 600 }}>no cost</span>
                <div style={{ color: T.muted, fontSize: 11 }}>{r.noCostScope === "contractor" ? "whole contractor, this project" : "this item"}</div>
              </span>
              {editable ? <ItemRateEditor row={r} companyName={contractor.companyName} onChanged={onChanged} /> : null}
            </div>
          );
        }
        return (
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "flex-end", gap: 2 }}>
            <span>
              {v == null ? <span style={{ color: T.muted }}>–</span> : <span style={overridden ? { fontWeight: 600 } : undefined}>{money(v)}/h</span>}
              <div style={{ color: overridden ? T.amber : T.muted, fontSize: 11 }}>{RATE_SOURCE[r.rateSource] || r.rateSource}</div>
              {overridden ? (
                <div style={{ color: T.muted, fontSize: 11 }}>
                  recorded {r.recordedRate == null ? "no rate" : `${money(r.recordedRate)}/h`}
                </div>
              ) : null}
            </span>
            {editable ? <ItemRateEditor row={r} companyName={contractor.companyName} onChanged={onChanged} /> : null}
          </div>
        );
      },
    },
    {
      title: "Cost", dataIndex: "cost", key: "cost", align: "right", width: 110,
      render: (v, r) => {
        if (r.hours == null) return <span style={{ color: T.muted }}>–</span>;
        if (v == null) return <span style={{ color: T.amber }}>unpriced</span>;
        if (r.noCost) return <span>{money(0)}<div style={{ color: T.muted, fontSize: 11 }}>no cost</div></span>;
        return money(v);
      },
    },
    ...(canEdit && withCost ? [{
      title: <Tooltip title={NO_COST_HELP}><span>No cost</span></Tooltip>,
      key: "nocost", width: 70, align: "center",
      render: (_, r) => (isRateEditable(r) ? <NoCostBox row={r} companyName={contractor.companyName} onChanged={onChanged} /> : null),
    }] : []),
    {
      title: "", key: "flags", width: 1,
      render: (_, r) => ((r.unidentified || []).length
        ? (r.unidentified.map((f) => <Tag key={f} color="orange" style={{ marginBottom: 2 }}>{f}</Tag>))
        : null),
    },
  ], ROW_SORT, rowSort);

  return (
    <div>
      {disagree ? (
        <Alert
          type="error" showIcon style={{ marginBottom: 8 }}
          message={`These rows add up to ${hrs(sumHours)} and ${money(sumCost)}, but the contractor total reads ${hrs(contractor.hours)} and ${money(contractor.cost)}. The rows are the record - report this.`}
        />
      ) : null}
      <Table
        size="small"
        rowKey="_k"
        dataSource={keyed}
        rowSelection={rowSelection}
        columns={columns}
        onChange={onRowSort}
        pagination={rows.length > 200 ? { pageSize: 200, showSizeChanger: false } : false}
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row>
              {/* One wider when the checkbox column is there, so the
                  totals stay under Hours and Cost. */}
              <Table.Summary.Cell index={0} colSpan={selectable ? 7 : 6}>
                <b>Total, {rows.length} line{rows.length === 1 ? "" : "s"}</b>
                {noHours ? (
                  <span style={{ color: T.muted, marginLeft: 8 }}>
                    {noHours} of them with no hours recorded, not in the total
                  </span>
                ) : null}
                {unpriced ? (
                  <span style={{ color: T.amber, marginLeft: 8 }}>
                    {unpriced} line{unpriced === 1 ? " has" : "s have"} no rate - counted in the hours, not in the cost, so the cost is a floor
                  </span>
                ) : null}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={6} align="right"><b>{hrs(sumHours)}</b></Table.Summary.Cell>
              <Table.Summary.Cell index={7} />
              <Table.Summary.Cell index={8} align="right">
                <b style={{ color: unpriced ? T.amber : undefined }}>{money(sumCost)}{unpriced ? " +" : ""}</b>
                {sumRecorded !== sumCost ? (
                  <div style={{ color: T.muted, fontSize: 11 }}>recorded {money(sumRecorded)}</div>
                ) : null}
                {noCostLines ? (
                  <div style={{ color: T.muted, fontSize: 11 }}>{noCostLines} line{noCostLines === 1 ? "" : "s"} no cost</div>
                ) : null}
              </Table.Summary.Cell>
              {canEdit && withCost ? <Table.Summary.Cell index={9} /> : null}
              <Table.Summary.Cell index={10} />
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
    </div>
  );
}
